import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OptionData } from '@/types/application.types';
import type { OptionSource } from '../utils/fieldOptions';
import styles from './SearchSelect.module.css';

/**
 * SearchSelect — searchable single-select dropdown.
 * Mirrors Flutter SearchDropDown → DropDownView → overlay list (OverlayDropDownBloc):
 * static options or an option source, a search box, 500ms debounced server search,
 * and the next page loaded when the list is scrolled to the end.
 */

interface SearchSelectProps {
  value: string;
  /** Label for the current value when it isn't in the loaded options (Flutter displayValue). */
  displayValue?: string;
  options: OptionData[];
  source?: OptionSource | null;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  enableSearch?: boolean;
  ariaLabel?: string;
  /** Maps an option to its visible label (e.g. localized experience units). */
  formatLabel?: (option: OptionData) => string;
  onSelect: (key: string, displayKey: string) => void;
  onBlur?: () => void;
}

const SEARCH_DEBOUNCE_MS = 500;

export function SearchSelect({
  value,
  displayValue,
  options,
  source,
  placeholder,
  disabled,
  invalid,
  enableSearch = true,
  ariaLabel,
  formatLabel,
  onSelect,
  onBlur,
}: SearchSelectProps) {
  const { t } = useTranslation();
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [remote, setRemote] = useState<OptionData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [picked, setPicked] = useState<OptionData | null>(null);

  const pageRef = useRef(1);
  const exhaustedRef = useRef(false);
  const requestRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const label = useCallback(
    (opt: OptionData) => (formatLabel ? formatLabel(opt) : opt.displayKey || opt.key),
    [formatLabel],
  );

  // ── Loading from the option source ──
  const load = useCallback(
    async (text: string, page: number) => {
      if (!source) return;
      const requestId = ++requestRef.current;
      setIsLoading(true);
      try {
        const data = await source.load(source.searchable ? text : '', page);
        if (requestId !== requestRef.current) return;
        if (data.length === 0) exhaustedRef.current = true;
        setRemote((prev) => (page === 1 ? data : [...prev, ...data]));
      } catch {
        if (requestId === requestRef.current) exhaustedRef.current = true;
      } finally {
        if (requestId === requestRef.current) setIsLoading(false);
      }
    },
    [source],
  );

  const open = () => {
    if (disabled || isOpen) return;
    setIsOpen(true);
    setQuery('');
    setActiveIndex(-1);
    if (source && remote.length === 0) {
      pageRef.current = 1;
      exhaustedRef.current = false;
      void load('', 1);
    }
  };

  /** `selected` skips the blur callback — the parent validates the new value on change. */
  const close = useCallback(
    (selected = false) => {
      setIsOpen(false);
      clearTimeout(debounceRef.current);
      if (!selected) onBlur?.();
    },
    [onBlur],
  );

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isOpen, close]);

  useEffect(() => {
    if (isOpen && enableSearch) searchRef.current?.focus();
  }, [isOpen, enableSearch]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const handleQuery = (text: string) => {
    setQuery(text);
    setActiveIndex(-1);
    if (source?.searchable) {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        pageRef.current = 1;
        exhaustedRef.current = false;
        void load(text.trim(), 1);
      }, SEARCH_DEBOUNCE_MS);
    }
  };

  // Server-searched lists are shown as returned; static / non-searchable lists are filtered here
  const visible = useMemo(() => {
    const list = source ? remote : options;
    if (source?.searchable) return list;
    const q = query.trim().toLowerCase();
    return q ? list.filter((opt) => label(opt).toLowerCase().includes(q)) : list;
  }, [source, remote, options, query, label]);

  const handleScroll = () => {
    const el = listRef.current;
    if (!el || !source?.pageable || isLoading || exhaustedRef.current) return;
    if (el.scrollTop + el.clientHeight < el.scrollHeight - 24) return;
    pageRef.current += 1;
    void load(query.trim(), pageRef.current);
  };

  const choose = (opt: OptionData) => {
    setPicked(opt);
    onSelect(opt.key, opt.displayKey);
    close(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        open();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, visible.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && visible[activeIndex]) choose(visible[activeIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };

  // Keep the active option in view while navigating with the keyboard
  useEffect(() => {
    if (activeIndex < 0) return;
    listRef.current?.children[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const known = [...options, ...remote].find((opt) => opt.key === value);
  const selectedLabel = value
    ? picked?.key === value
      ? label(picked)
      : known
        ? label(known)
        : displayValue || value
    : '';

  return (
    <div ref={rootRef} className={styles.root} onKeyDown={handleKeyDown}>
      <button
        type="button"
        className={`${styles.trigger} ${invalid ? styles.invalid : ''} ${isOpen ? styles.triggerOpen : ''}`}
        onClick={() => (isOpen ? close() : open())}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listId : undefined}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
      >
        <span className={selectedLabel ? styles.value : styles.placeholder}>
          {selectedLabel || placeholder || t('Select')}
        </span>
        <svg className={styles.chevron} width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">
          <path d="M1 1.5 6 6.5l5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {isOpen && (
        <div className={styles.popover}>
          {enableSearch && (
            <div className={styles.searchRow}>
              <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
                <circle cx="9" cy="9" r="6" />
                <path d="M13.5 13.5 17 17" />
              </svg>
              <input
                ref={searchRef}
                className={styles.search}
                value={query}
                onChange={(e) => handleQuery(e.target.value)}
                placeholder={t('Search')}
                aria-controls={listId}
                aria-autocomplete="list"
              />
            </div>
          )}
          <ul id={listId} ref={listRef} className={styles.list} role="listbox" onScroll={handleScroll}>
            {visible.map((opt, i) => {
              const isSelected = opt.key === value;
              return (
                <li
                  key={`${opt.key}-${i}`}
                  role="option"
                  aria-selected={isSelected}
                  className={`${styles.option} ${i === activeIndex ? styles.optionActive : ''} ${isSelected ? styles.optionSelected : ''}`}
                  onMouseEnter={() => setActiveIndex(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choose(opt)}
                >
                  <span className={styles.optionLabel}>{label(opt)}</span>
                  {isSelected && (
                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="m4.5 10.5 3.5 3.5 7.5-8" />
                    </svg>
                  )}
                </li>
              );
            })}
            {isLoading && (
              <li className={styles.status} aria-live="polite">
                <span className={styles.spinner} />
                {t('Loading...')}
              </li>
            )}
            {/* Flutter's empty text for form search lists (generic_widgets emptyBuilder) */}
            {!isLoading && visible.length === 0 && (
              <li className={styles.status}>{t('No data found')}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
